<template>
  <v-dialog
    :value="value"
    width="500"
    @input="handleInput"
  >
    <v-form @submit.prevent.stop="submit">
      <v-card>
        <v-toolbar
          flat
          dark
          color="primary"
        >
          {{ title }}
          <v-spacer />
          <v-btn
            icon
            dark
            @click="$emit('input', false)"
          >
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-toolbar>

        <v-card-text>
          <v-text-field
            ref="name"
            v-model="form.name"
            :label="$t('Enter category name')"
            class="mb-2"
            hide-details
            outlined
            autofocus
          />

          <div class="d-flex justify-center mb-2">
            <v-btn-toggle
              v-model="selectCustomIcon"
              dense
              tile
              color="primary"
              group
              mandatory
            >
              <v-btn :value="false">
                {{ $t('Built-in icons') }}
              </v-btn>

              <v-btn :value="true">
                {{ $t('Media library') }}
              </v-btn>
            </v-btn-toggle>
          </div>

          <fa-icon-picker
            v-if="! selectCustomIcon"
            v-model="form.icon"
          />

          <v-autocomplete
            v-else
            v-model="form.icon_upload_id"
            :loading="form.processing"
            :error-messages="form.errors.icon_upload_id"
            :items="images"
            :label="$t('Select icon from media library')"
            item-text="title"
            item-value="id"
            outlined
            clearable
            :hint="$t('SVG format is recommended')"
            persistent-hint
          />

          <v-textarea
            v-model="form.description"
            outlined
            class="mt-7"
            :label="$t('Enter a short description')"
            hide-details
            rows="3"
          />
        </v-card-text>

        <v-divider />

        <v-card-actions>
          <v-spacer />
          <v-btn
            color="grey"
            text
            @click="$emit('input', false)"
          >
            {{ $t('Cancel') }}
          </v-btn>
          <v-btn
            color="primary"
            text
            type="submit"
            :loading="form.processing"
            :disabled="form.processing || form.name.length === 0"
          >
            {{ $t('Save') }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-form>
  </v-dialog>
</template>

<script>
export default {
  props: {
    value: {
      type: Boolean
    },
    faqId: {
      type: Number,
      required: true
    },
    title: {
      type: String,
      required: false,
      default: null
    },
    mode: {
      type: String,
      required: false,
      default: 'create'
    },
    preserveState: {
      type: Boolean,
      default: false
    },
    categoryId: {
      type: Number,
      required: false,
      default: null
    },
    category: {
      type: Object,
      required: false,
      default: null
    },
    isSubcategory: {
      type: Boolean
    },
    images: {
      type: Array,
      required: false,
      default: () => []
    }
  },

  data() {
    return {
      form: this.$inertia.form({
        name: '',
        icon: '',
        icon_upload_id: null,
        description: '',
      }),
      selectCustomIcon: false,
    }
  },

  watch: {
    category: function (category) {
      if (category && this.mode === 'edit') {
        this.form.name = category.default_translation.name;
        this.form.description = category.default_translation.description;
        this.form.icon = category.icon;
        this.form.icon_upload_id = category.icon_upload_id;

        this.selectCustomIcon = this.form.icon_upload_id !== null;
      } else {
        this.form.reset();
        this.form.clearErrors();
      }
    }
  },

  methods: {
    submit() {
      switch (this.mode) {
        case 'create':
          this.handleCreate();
          break;

        case 'edit':
          this.handleUpdate();
          break;
      }
    },

    handleCreate() {
      let url;
      if (!this.isSubcategory) {
        url = this.route('faq.category.store', this.faqId);
      } else {
        const catId = this.category?.id || this.categoryId;
        url = this.route('faq.subcategory.store', [this.faqId, catId]);
      }

      this.form.post(url, {
        preserveState: this.preserveState,
        preserveScroll: true,
        onSuccess: () => {
          this.$emit('create-success');

          this.$root.refreshFaqPreview();

          this.handleInput(false);
        }
      });
    },

    handleUpdate() {
      const routeName = this.isSubcategory ? 'faq.subcategory.update' : 'faq.category.update';
      const url = this.route(routeName, [this.faqId, this.category.id]);

      this.form.patch(url, {
        preserveState: this.preserveState,
        preserveScroll: true,
        onSuccess: () => {
          this.$emit('update-success');

          this.$root.refreshFaqPreview();

          this.handleInput(false);
        }
      });
    },

    handleInput(value) {
      this.$emit('input', value);

      if (value === false) {
        this.$nextTick(() => {
          this.form.reset();
          this.form.clearErrors();
        });
      }
    }
  }
}
</script>